// React
import { ReactElement, useEffect, useState } from 'react';
// Modules
import { DateRange, Range, RangeKeyDict } from 'react-date-range';
// @ts-ignore
import * as locales from 'react-date-range/dist/locale';
// Interfaces
import { LangEnum } from '@utmb/types/enum/LangEnum';
// Styles
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

interface DateRangePickerProps {
    onChange: (selectedDateMin: Date, selectedDateMax: Date) => void;
    locale: LangEnum;
    minDate: Date;
    maxDate: Date;
    selectedDateMin: Date;
    selectedDateMax: Date;
}

const DateRangePicker = ({ minDate, maxDate, onChange, selectedDateMin, selectedDateMax, locale }: DateRangePickerProps): ReactElement => {
    const handleDateChange = (event: RangeKeyDict): void => {
        setDates([event.selection]);

        onChange(event.selection.startDate ?? minDate, event.selection.endDate ?? maxDate);
    };

    const localeMapper: {
        [K in LangEnum]: string;
    } = {
        [LangEnum.AR]: 'ar',
        [LangEnum.CA]: 'ca',
        [LangEnum.DE]: 'de',
        [LangEnum.EL]: 'el',
        [LangEnum.EN]: 'enGB',
        [LangEnum.ES]: 'es',
        [LangEnum.EU]: 'es',
        [LangEnum.FR]: 'fr',
        [LangEnum.ID]: 'id',
        [LangEnum.IT]: 'it',
        [LangEnum.JA]: 'ja',
        [LangEnum.KO]: 'ko',
        [LangEnum.LV]: 'lv',
        [LangEnum.OC]: 'es',
        [LangEnum.PL]: 'pl',
        [LangEnum.PT]: 'pt',
        [LangEnum.RO]: 'ro',
        [LangEnum.RU]: 'ru',
        [LangEnum.HR]: 'hr',
        [LangEnum.TH]: 'th',
        [LangEnum.TR]: 'tr',
        [LangEnum.SL]: 'sl',
        [LangEnum.SV]: 'sv',
        [LangEnum.VI]: 'vi',
        [LangEnum.ZH_SIMPLIFY]: 'zhCN',
        [LangEnum.ZH_TRADITIONAL]: 'zhTW',
    };

    const [dates, setDates] = useState<Range[]>([
        {
            startDate: selectedDateMin,
            endDate: selectedDateMax,
            key: 'selection',
        },
    ]);

    useEffect(() => {
        setDates([
            {
                startDate: selectedDateMin,
                endDate: selectedDateMax,
                key: 'selection',
            },
        ]);
    }, [selectedDateMin, selectedDateMax]);

    return (
        <>
            <DateRange
                locale={locales[localeMapper[locale]]}
                onChange={handleDateChange}
                editableDateInputs={true}
                moveRangeOnFirstSelection={false}
                retainEndDateOnFirstSelection={true}
                ranges={dates}
                showMonthAndYearPickers={true}
                direction="vertical"
                minDate={minDate}
                maxDate={maxDate}
                dateDisplayFormat="yyyy-MM-dd"
            />
            <style>
                {`
            .rdrDateDisplayItem > input {
                color: var(--utmb-color-primary)!important;
                font-family: var(--utmb-font-oxanium)!important;
                cursor: text!important;
            }
            .rdrDateDisplayItemActive{
                border-color: var(--utmb-color-secondary)!important;
                border-width: 2px
            }
            .rdrYearPicker > select {
                font-family: var(--utmb-font-oxanium)!important;
            }
            .rdrMonthPicker > select {
                font-family: var(--utmb-font-oxanium)!important;
            }
            .rdrWeekDay {
                font-family: var(--utmb-font-futura-book)!important;
                color: var(--utmb-color-primary)!important;
            }
            .rdrDay {
                font-family: var(--utmb-font-oxanium)!important;
            }
            .rdrStartEdge, .rdrInRange, .rdrEndEdge {
                color: var(--utmb-color-primary)!important;
            }
        `}
            </style>
        </>
    );
};

export default DateRangePicker;
